import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { useLetterPartyPhaserGame } from '@/features/letterParty/hooks/useLetterPartyPhaserGame';
import { getDevicePixelRatio } from '@/features/letterParty/game/utils/canvasSize';
import { useLetterPartyTheme } from '@/features/letterParty/theme';

import type { ConfettiCollectible, Direction, HatCollectible, LetterQ, Position } from '@/features/letterParty/types';

export const GRID_WIDTH_PORTRAIT = 8;
export const GRID_HEIGHT_PORTRAIT = 12;
export const GRID_WIDTH_LANDSCAPE = 12;
export const GRID_HEIGHT_LANDSCAPE = 8;

const MIN_CELL_SIZE = 20;
const MAX_CELL_SIZE = 96;
const BOARD_PADDING = 32;
const CONTROL_BAR_GAP = 16;
const RENDER_PADDING_CELL_RATIO = 0.65;
const CONSTRAINED_GRID_WIDTH_RATIO = 0.6;
const CONSTRAINED_GRID_WIDTH_MEDIA_QUERY = '(min-width: 1024px), (min-width: 768px) and (orientation: landscape)';

interface GameBoardProps {
	gridWidth: number;
	gridHeight: number;
	isPortrait: boolean;
	isMobile: boolean;
	canRotateEntities: boolean;
	snake: Position[];
	currentDirection: Direction;
	hatCount: number;
	hats: HatCollectible[];
	confettis: ConfettiCollectible[];
	letterQs: LetterQ[];
	isRecovering: boolean;
	waitingForFirstDirection?: boolean;
	isStopped?: boolean;
	onRotateEntities?: (rotatePosition: (position: Position) => Position) => void;
	onSwipeDirection: (direction: Direction) => void;
}

export interface GameBoardHandle {
	getCellCenterPosition: (position: Position) => { x: number; y: number } | null;
}

function getGridSizingWidth(wrapperWidth: number): number {
	if (typeof window !== 'undefined' && window.matchMedia(CONSTRAINED_GRID_WIDTH_MEDIA_QUERY).matches) {
		return window.innerWidth * CONSTRAINED_GRID_WIDTH_RATIO;
	}

	return wrapperWidth;
}

const GameBoard = forwardRef<GameBoardHandle, GameBoardProps>(
	(
		{
			gridWidth,
			gridHeight,
			isPortrait,
			isMobile,
			canRotateEntities,
			snake,
			currentDirection,
			hatCount,
			hats,
			confettis,
			letterQs,
			isRecovering,
			waitingForFirstDirection = false,
			isStopped = false,
			onRotateEntities,
			onSwipeDirection,
		},
		ref,
	) => {
		const theme = useLetterPartyTheme();
		const [cellSize, setCellSize] = useState(45);
		const [playAreaTop, setPlayAreaTop] = useState(0);
		const boardWrapperRef = useRef<HTMLDivElement | null>(null);
		const boardRef = useRef<HTMLDivElement | null>(null);
		const previousOrientationRef = useRef(isPortrait);
		const pixelRatio = getDevicePixelRatio();
		const boardState = {
			gridWidth,
			gridHeight,
			cellSize,
			playAreaTop,
			pixelRatio,
			snake,
			currentDirection,
			hatCount,
			hats,
			confettis,
			letterQs,
			isRecovering,
			waitingForFirstDirection,
			isStopped,
		};

		useLetterPartyPhaserGame({
			boardState,
			containerRef: boardRef,
			onSwipeDirection,
			theme,
		});

		const calculateCellSize = useCallback(() => {
			if (!boardWrapperRef.current) return;

			const wrapperRect = boardWrapperRef.current.getBoundingClientRect();
			const screenElement = boardWrapperRef.current.closest('.letter-party-game__screen');
			const controlBarElement = screenElement?.querySelector('.letter-party-game__control-bar');
			const controlBarRect = controlBarElement?.getBoundingClientRect();
			const nextPlayAreaTop = controlBarRect
				? Math.max(0, controlBarRect.bottom - wrapperRect.top + CONTROL_BAR_GAP)
				: 0;
			const availableWidth = Math.max(0, getGridSizingWidth(wrapperRect.width) - BOARD_PADDING);
			const availableHeight = Math.max(0, wrapperRect.height - nextPlayAreaTop - BOARD_PADDING);
			const paddedGridWidth = gridWidth + RENDER_PADDING_CELL_RATIO * 2;
			const paddedGridHeight = gridHeight + RENDER_PADDING_CELL_RATIO * 2;

			const cellSizeByWidth = availableWidth / paddedGridWidth;
			const cellSizeByHeight = availableHeight / paddedGridHeight;
			const optimalCellSize = Math.min(cellSizeByWidth, cellSizeByHeight);
			const sanitizedOptimal = Number.isFinite(optimalCellSize) ? optimalCellSize : MIN_CELL_SIZE;
			const clampedCellSize = Math.max(MIN_CELL_SIZE, Math.min(MAX_CELL_SIZE, sanitizedOptimal));

			setPlayAreaTop(nextPlayAreaTop);
			setCellSize(Math.max(MIN_CELL_SIZE, Math.floor(clampedCellSize)));
		}, [gridWidth, gridHeight]);

		useEffect(() => {
			calculateCellSize();
		}, [calculateCellSize, isMobile]);

		useEffect(() => {
			window.addEventListener('resize', calculateCellSize);
			return () => window.removeEventListener('resize', calculateCellSize);
		}, [calculateCellSize]);

		useEffect(() => {
			if (!onRotateEntities) return;
			if (!canRotateEntities) {
				previousOrientationRef.current = isPortrait;
				return;
			}

			if (previousOrientationRef.current === isPortrait) return;

			const oldIsPortrait = previousOrientationRef.current;
			previousOrientationRef.current = isPortrait;

			const oldWidth = oldIsPortrait ? GRID_WIDTH_PORTRAIT : GRID_WIDTH_LANDSCAPE;
			const oldHeight = oldIsPortrait ? GRID_HEIGHT_PORTRAIT : GRID_HEIGHT_LANDSCAPE;
			const newWidth = isPortrait ? GRID_WIDTH_PORTRAIT : GRID_WIDTH_LANDSCAPE;
			const newHeight = isPortrait ? GRID_HEIGHT_PORTRAIT : GRID_HEIGHT_LANDSCAPE;

			const rotatePosition = (pos: Position): Position => {
				let newX: number;
				let newY: number;

				if (isPortrait) {
					newX = oldHeight - 1 - pos.y;
					newY = pos.x;
				} else {
					newX = pos.y;
					newY = oldWidth - 1 - pos.x;
				}

				return {
					x: Math.max(0, Math.min(newWidth - 1, newX)),
					y: Math.max(0, Math.min(newHeight - 1, newY)),
				};
			};

			onRotateEntities(rotatePosition);
		}, [isPortrait, canRotateEntities, onRotateEntities]);

		useImperativeHandle(
			ref,
			() => ({
				getCellCenterPosition: (position: Position) => {
					if (!boardRef.current) return null;
					const boardRect = boardRef.current.getBoundingClientRect();
					const gridPixelWidth = gridWidth * cellSize;
					const gridPixelHeight = gridHeight * cellSize;
					const availableHeight = Math.max(gridPixelHeight, boardRect.height - playAreaTop);
					const originX = (boardRect.width - gridPixelWidth) / 2;
					const originY = playAreaTop + (availableHeight - gridPixelHeight) / 2;

					return {
						x: boardRect.left + originX + (position.x + 0.5) * cellSize,
						y: boardRect.top + originY + (position.y + 0.5) * cellSize,
					};
				},
			}),
			[cellSize, gridHeight, gridWidth, playAreaTop],
		);

		return (
			<div ref={boardWrapperRef} className="letter-party-game__board-wrapper">
				<div ref={boardRef} className="letter-party-game__board" />
			</div>
		);
	},
);

GameBoard.displayName = 'GameBoard';

export default GameBoard;
